// ui/composables/useBillingWorklist.js
import { getCurrentScope, onScopeDispose, ref } from 'vue';

import { isAccessDenied, watchBillingItems } from '../../infrastructure/api/billingItemApi';

/**
 * A clinic's billing items, as the worklist works with them.
 *
 * THE CONTEXT'S COMPOSITION ROOT for billing items — the one place that binds the Firestore adapter
 * to the repository port. Everything above depends on the port; nothing above imports
 * `infrastructure/`. The same role `useChargeMaster` plays for sheets.
 *
 * LIVE, BECAUSE A FIX HAPPENS ELSEWHERE. A biller corrects a coverage or a payer in its own editor,
 * the projector re-derives the items, and the row turns ready or shows its next reason without the
 * page asking again. Offline the adapter delivers the cached set once instead.
 *
 * IT LOADS AND NOTHING ELSE. Narrowing — by period, by payer, by status, by export state — belongs
 * to the table, in memory, so changing any of it never re-reads Firestore. This exposes no
 * filtering of its own on purpose: two places narrowing the same list is how a screen comes to
 * disagree with its own export about what a biller is looking at.
 */

// eslint-disable-next-line import/prefer-default-export
export function useBillingWorklist() {
  /** @type {import('vue').Ref<import('../../domain/model/BillingItem.types').BillingItem[]>} */
  const items = ref([]);
  const loading = ref(false);
  const denied = ref(false);
  let stopWatching = () => {};

  /**
   * Follow one clinic's items until the scope ends or another clinic is followed.
   *
   * A FAILURE CLEARS THE LIST rather than leaving the last rows up, and is handed to the caller,
   * which has to say something: an empty table and a failed read look identical.
   *
   * A DENIED READ IS NOT HANDED ON. The billing privilege can be taken away while the page is open,
   * and "try again" cannot help; `denied` says so instead, for the page to show in place.
   *
   * @param {{clinicId: string, onError?: (error: Error) => void}} options
   */
  function subscribe({ clinicId, onError }) {
    stopWatching();
    loading.value = true;
    denied.value = false;
    items.value = [];

    stopWatching = watchBillingItems(
      { clinicId },
      (next) => {
        items.value = next;
        loading.value = false;
      },
      (error) => {
        items.value = [];
        loading.value = false;
        denied.value = isAccessDenied(error);
        if (!denied.value) {
          onError?.(error);
        }
      },
    );
  } // subscribe()

  if (getCurrentScope()) {
    onScopeDispose(() => stopWatching());
  }

  return {
    items,
    loading,
    denied,
    subscribe,
  };
} // useBillingWorklist()
